import React, { useMemo, useEffect, useLayoutEffect, useState } from 'react'
import { View, Image, LayoutAnimation } from 'react-native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { Recommender } from '@dialogue/services'
import styled, { useTheme } from 'styled-components/native'

import PatientHistoryRedux from 'APP/Redux/PatientHistoryRedux'
import WellBeingRedux from 'APP/Redux/WellBeingRedux'
import I18n from 'APP/I18n'
import sprigClient from 'APP/Services/Sprig'
import { isMobile, isWeb } from 'APP/Helpers/checkPlatform'

import { Images } from 'APP/Themes'
import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import DialogueMarkdown from 'APP/Components/DialogueMarkdown'
import NetworkWrapper from 'APP/Components/NetworkWrapper'
import { ResponsiveScrollView, ResponsiveView } from 'APP/Converse/Layout'
import Button from 'APP/Converse/Button'
import Typography from 'APP/Converse/Typography'
import RecommendationGroup from './RecommendationGroup'
import Slider from './Slider'
import { animationConfiguration } from './helpers'
import Styles, { MarkdownStyles } from './style'

const GROUPS = {
  STANDALONE: 'STANDALONE',
  REST: 'REST',
}

const ButtonContainer = styled.View`
  margin-horizontal: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-bottom: ${({ theme }) => theme.insets.bottom}px;
`

const CenteredTextContainer = styled(Typography)`
  text-align: center;
`

export function WellBeingIndexCardEmpty() {
  return (
    <View style={Styles.wellBeingIndexCard} testID="WELLBEING_INDEX_CARD_EMPTY">
      <CenteredTextContainer variant="h3">
        {I18n.t('WellBeingIndexScreen.wellBeingEmptyCardTitle')}
      </CenteredTextContainer>
      <Image style={Styles.emptyChartImage} source={Images.wellBeing.emptyChart} />
      <DialogueMarkdown
        styles={MarkdownStyles}
        markdown={I18n.t('WellBeingIndexScreen.wellBeingEmptyCardDesc')}
      />
    </View>
  )
}

function WellBeingIndexScreen(props) {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { navigation, route } = props
  // result is passed when a member complets the assessment
  // and launches a deeplink to this screen
  const { result } = { ...route?.params, ...props }
  const assessmentWasJustCompleted = Boolean(result)

  const [isScrollEnabled, setIsScrollEnabled] = useState(true)
  const recommendationStore = useSelector((state) => state.wellBeingAssessment.recommendations)
  const data = recommendationStore.data
  const noRecommendations = !data?.length
  const retry = recommendationStore.retry
  const loading = (assessmentWasJustCompleted || noRecommendations) && recommendationStore.loading
  const isBottomCTAVisible = !loading && !assessmentWasJustCompleted

  const [recommendationGroups, score, score_range, focus] = useMemo(() => {
    const sources =
      data?.find(({ type }) => type === Recommender.Types.RecommendationType.PROGRESS)?.sources ||
      {}
    const nextFocus = data?.find(({ type }) => type === Recommender.Types.RecommendationType.FOCUS)
      ?.sources?.focus

    const nextRecommendationGroups = (data || []).reduce((acc, recommendation) => {
      let key = GROUPS.REST
      if (
        [
          Recommender.Types.RecommendationType.PROGRESS,
          Recommender.Types.RecommendationType.COMPARE,
          Recommender.Types.RecommendationType.FOCUS,
        ].includes(recommendation.type)
      ) {
        key = GROUPS.STANDALONE
      }
      return {
        ...acc,
        [key]: [...(acc[key] || []), key === GROUPS.STANDALONE ? [recommendation] : recommendation],
      }
    }, {})

    return [nextRecommendationGroups, sources.score, sources.score_range, nextFocus]
  }, [data])

  const getOrCreateWbiEpisode = () => {
    dispatch(PatientHistoryRedux.getOrCreateWbiEpisodeRequest())
  }
  const getRecommendations = () => dispatch(WellBeingRedux.getRecommendations())

  // sprig analytics
  useEffect(() => {
    if (recommendationStore.loading) return
    if (score_range && assessmentWasJustCompleted) {
      sprigClient.track('WBI_SCORE_' + String(score_range).toUpperCase())
    }
    if (!score_range) {
      sprigClient.track('WBI_SCORE_NONE')
    }
  }, [recommendationStore.loading, assessmentWasJustCompleted, score_range])

  useEffect(() => {
    getRecommendations()
  }, [result])

  useLayoutEffect(() => {
    navigation.setOptions({
      onBackButtonPress: () => {
        sprigClient.track('WBI_SCREEN_EXIT')
        // only once a user has completed the WBI assessment from MH -> Monitor your well-being,
        // navigate back twice to Monitor your well-being
        if (!isBottomCTAVisible) {
          navigation.pop(2)
          return
        }
        navigation.goBack()
      },
    })
  })

  useLayoutEffect(() => {
    !noRecommendations &&
      animationConfiguration &&
      LayoutAnimation.configureNext(animationConfiguration)
    if (isMobile()) {
      navigation.setOptions({
        title: noRecommendations ? I18n.t('WellBeingIndexScreen.title') : undefined,
      })
    }
  }, [noRecommendations])

  const getCtaTextLabel = () =>
    noRecommendations
      ? 'WellBeingIndexScreen.wellBeingCardGetScoreCta'
      : 'WellBeingIndexScreen.wellBeingCardCheckinCta'

  const ctaTextLabel = getCtaTextLabel()
  const groupProps = {
    score,
    score_range,
    focus,
    onSetScrollEnabled: setIsScrollEnabled,
  }

  const BottomCta = () => (
    <ButtonContainer>
      <Button
        variant="primary"
        widthVariant="full"
        testID="WELLBEING_INDEX_CTA"
        title={I18n.t(ctaTextLabel)}
        analyticsName={ctaTextLabel}
        onPress={() => {
          sprigClient.track('WBI_ASSESSMENT_STARTED')
          getOrCreateWbiEpisode()
        }}
      />
    </ButtonContainer>
  )

  const WrapperComponent = isWeb() || noRecommendations ? ResponsiveScrollView : ResponsiveView

  return (
    <NoticeBarContainer>
      <NetworkWrapper loading={loading} error={retry} refresh={getRecommendations}>
        <WrapperComponent style={Styles.flexGrow}>
          {noRecommendations && <WellBeingIndexCardEmpty />}
          {!noRecommendations && (
            <Slider
              isScrollEnabled={isScrollEnabled}
              slideOffset={theme.fonts.lineHeight.h1 * 2}
              bottomCta={isBottomCTAVisible && <BottomCta />}
            >
              {Object.entries(recommendationGroups)
                .map(([key, recommendationGroup]) =>
                  key === GROUPS.STANDALONE ? (
                    recommendationGroup.map((recommendations, i) => (
                      <RecommendationGroup
                        spaceAround
                        key={`${GROUPS.STANDALONE}-${i}`}
                        recommendations={recommendations}
                        {...groupProps}
                      />
                    ))
                  ) : (
                    <RecommendationGroup
                      spaceAround
                      key={GROUPS.REST}
                      recommendations={recommendationGroup}
                      {...groupProps}
                    />
                  )
                )
                .flat()}
            </Slider>
          )}
        </WrapperComponent>

        {noRecommendations && isBottomCTAVisible && (
          <ResponsiveView>
            <BottomCta />
          </ResponsiveView>
        )}
      </NetworkWrapper>
    </NoticeBarContainer>
  )
}

export default WellBeingIndexScreen
